import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {FaIcon} from '../../FaIcon';
import {Spinner} from '../../Spinner';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {FACT_TYPE_ORDER, FACT_TYPE_MODIFIER, factTypeLabelKey} from '../memory/factConstants';
import {parseLocalDate} from '../memory/MemoryResumenView';

// Los hechos más "accionables" primero (mismo orden de prioridad que la vista Hechos).
const PREVIEW_FACTS = 3;

// Card "Memoria" del Panel (ficha v2, D10): el mini-brief que hace visible qué sabe Dashbo
// del cliente sin salir del morning briefing — 2-3 hechos clave + pendientes + última reunión.
export function MemoryPreviewBox({clientId}) {
    const {t, i18n} = useTranslation();
    const [brief, setBrief] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        ClientMemoryApi.getMemoryBrief(clientId).then((data) => {
            if (data && data.status === 'OK') {
                setBrief(data.data);
            }
            setLoading(false);
        });
    }, [clientId]);

    if (loading) {
        return (
            <div className="box memory-preview-box">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    if (!brief) return null; // sin acceso / error: la card no aporta — no ensuciar el Panel

    const facts = brief.facts || [];
    const topFacts = FACT_TYPE_ORDER
        .flatMap((type) => facts.filter((f) => f.fact_type === type).map((f) => ({...f, type})))
        .slice(0, PREVIEW_FACTS);
    const meetings = brief.meetings || {};
    const lastMeeting = meetings.last_meeting;
    const isEmpty = topFacts.length === 0 && (brief.notes || []).length === 0;

    return (
        <div className="box memory-preview-box">
            <div className="activity-log-preview-header">
                <h3 className="activity-log-preview-title">{t('control_central.memory_title')}</h3>
                <Link className="memory-preview-link" to={`/client/${clientId}/memory`}>
                    {t('control_central.memory_see_all')} &rarr;
                </Link>
            </div>

            {isEmpty ? (
                <div className="cc-empty-state">
                    <FaIcon iconName="brain"/>
                    <p>{t('control_central.memory_empty')}</p>
                    <Link className="btn -alt -small" to={`/client/${clientId}/memory/setup`}>
                        <FaIcon iconName="plus"/> {t('control_central.memory_empty_cta')}
                    </Link>
                </div>
            ) : (
                <>
                    {topFacts.map((fact) => (
                        <p key={fact.id} className={`memory-preview-fact ${FACT_TYPE_MODIFIER[fact.fact_type] || ''}`}>
                            <strong>{t(factTypeLabelKey(fact.fact_type))}:</strong> {fact.statement}
                        </p>
                    ))}
                    {topFacts.length === 0 && (brief.notes || []).slice(0, 1).map((note) => (
                        <p key={note.id} className="memory-preview-fact">{note.text}</p>
                    ))}
                    <div className="memory-preview-footer">
                        {brief.pending_facts_count > 0 && (
                            <Link className="memory-preview-pending" to={`/client/${clientId}/memory/facts`}>
                                {t('control_central.memory_pending', {count: brief.pending_facts_count})}
                            </Link>
                        )}
                        {lastMeeting && (
                            <span className="memory-preview-meta">
                                {t('control_central.memory_last_meeting', {
                                    // Fecha de la REUNIÓN (occurred_on, date-only → parse local),
                                    // no de la carga; fallback a created_on para docs viejos.
                                    date: parseLocalDate(lastMeeting.occurred_on || lastMeeting.created_on)
                                        .toLocaleDateString(i18n.language),
                                    interpolation: {escapeValue: false},
                                })}
                            </span>
                        )}
                        <Link className="btn -alt -negative -small" to={`/client/${clientId}/memory/setup`}>
                            {t('control_central.memory_add_meeting')}
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
}
